import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Plus,
  Ban,
  Check,
  X,
  Pencil,
  Trash2,
  Wallet,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { bookingService, hostService, listingService } from "@/lib/services";
import { extractErrorMessage } from "@/lib/api";
import { ChangeDatesDialog } from "@/components/change-dates-dialog";
import { formatUsh } from "@/lib/format";
import type { Booking, BookingStatus, Listing, Reservation } from "@/types/api";

export const Route = createFileRoute("/_authenticated/host/calendar")({
  component: HostCalendarPage,
});

// ---------------- helpers ----------------

function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function daysInMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
}
function ymd(d: Date) {
  return d.toISOString().slice(0, 10);
}
function parseYMD(s: string) {
  const [y, m, day] = s.split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, day ?? 1);
}
function isoDayList(month: Date): string[] {
  const total = daysInMonth(month);
  const list: string[] = [];
  for (let i = 1; i <= total; i++) {
    list.push(ymd(new Date(month.getFullYear(), month.getMonth(), i)));
  }
  return list;
}
function nightsBetweenISO(a: string, b: string) {
  const aT = parseYMD(a).getTime();
  const bT = parseYMD(b).getTime();
  return Math.max(0, Math.round((bT - aT) / 86_400_000));
}

const STATUS_CLASSES: Record<BookingStatus | "blocked", string> = {
  // A host block is deliberately drab and hatched-looking: it is not a sale,
  // and it should never read like one at a glance across the month grid.
  blocked: "bg-ink-subtle/25 text-ink-muted border-ink-subtle/40",
  pending: "bg-accent/55 text-secondary border-accent",
  processing: "bg-muted text-ink-muted border-border",
  confirmed: "bg-primary/80 text-primary-foreground border-primary",
  checked_in: "bg-primary text-primary-foreground border-primary",
  checked_out: "bg-muted text-ink-muted border-border",
  completed: "bg-muted text-ink-muted border-border",
  cancelled: "bg-destructive/15 text-destructive border-destructive/40 line-through",
  rejected: "bg-destructive/15 text-destructive border-destructive/40 line-through",
};

function unwrap<T>(res: unknown): T[] {
  if (!res) return [];
  const r = res as { data?: T[] };
  return Array.isArray(r.data) ? r.data : [];
}

// ---------------- page ----------------

function HostCalendarPage() {
  const qc = useQueryClient();
  const [month, setMonth] = useState<Date>(() => startOfMonth(new Date()));
  const [openRes, setOpenRes] = useState<{ res: Reservation; listing: Listing } | null>(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [editDatesFor, setEditDatesFor] = useState<string | null>(null);

  const listingsQ = useQuery({
    queryKey: ["host-listings"],
    queryFn: () => hostService.listings(),
  });
  const listings = useMemo(() => unwrap<Listing>(listingsQ.data), [listingsQ.data]);

  // Fetch reservations per listing in parallel — booking-detail price lives on the booking,
  // so we also need the bookings list to compute revenue.
  const reservationQs = useQueries({
    queries: listings.map((l) => ({
      queryKey: ["listing-reservations", l.id],
      queryFn: () => listingService.reservations(l.id),
      staleTime: 60_000,
    })),
  });

  const bookingsQ = useQuery({
    queryKey: ["host-bookings", { per_page: 200 }],
    queryFn: () => hostService.bookings({ per_page: 200 }),
  });
  const bookings: Booking[] = bookingsQ.data?.data ?? [];
  const bookingById = useMemo(() => {
    const m = new Map<string, Booking>();
    bookings.forEach((b) => m.set(b.id, b));
    return m;
  }, [bookings]);

  const days = isoDayList(month);
  const monthLabel = month.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const monthStart = ymd(month);
  const monthEnd = ymd(new Date(month.getFullYear(), month.getMonth() + 1, 0));

  const reservationsByListing = useMemo(() => {
    const out = new Map<string, Reservation[]>();
    listings.forEach((l, i) => {
      out.set(l.id, reservationQs[i]?.data?.data ?? []);
    });
    return out;
  }, [listings, reservationQs]);

  // Per-listing revenue for the visible month (sum of nightly price for nights inside month
  // for non-cancelled reservations).
  const revenueByListing = useMemo(() => {
    const map = new Map<string, number>();
    for (const l of listings) {
      const reservations = reservationsByListing.get(l.id) ?? [];
      let revenue = 0;
      const price = Number(l.price_per_night);
      for (const r of reservations) {
        if (r.status === "cancelled" || r.status === "rejected") continue;
        // A block holds nights without earning anything. Counting it would
        // report income the host never made.
        if (r.status === "blocked") continue;
        const start = r.check_in_date > monthStart ? r.check_in_date : monthStart;
        const endExclusive = r.check_out_date < monthEnd ? r.check_out_date : monthEnd;
        const n = Math.max(0, nightsBetweenISO(start, endExclusive));
        revenue += n * price;
      }
      map.set(l.id, revenue);
    }
    return map;
  }, [listings, reservationsByListing, monthStart, monthEnd]);

  const totalMonthRevenue = useMemo(
    () => Array.from(revenueByListing.values()).reduce((s, v) => s + v, 0),
    [revenueByListing],
  );

  // Mutations — only confirm/reject hit confirmed real endpoints.
  const confirm = useMutation({
    mutationFn: (id: string) => hostService.confirmBooking(id),
    onSuccess: () => {
      toast.success("Booking confirmed.");
      qc.invalidateQueries({ queryKey: ["host-bookings"] });
      qc.invalidateQueries({ queryKey: ["listing-reservations"] });
      setOpenRes(null);
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Couldn't confirm")),
  });
  const reject = useMutation({
    mutationFn: (id: string) => hostService.rejectBooking(id),
    onSuccess: () => {
      toast.success("Booking rejected.");
      qc.invalidateQueries({ queryKey: ["host-bookings"] });
      qc.invalidateQueries({ queryKey: ["listing-reservations"] });
      setOpenRes(null);
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Couldn't reject")),
  });
  /*
    A host withdrawing a confirmed stay is not the same as rejecting a request:
    the guest was already counting on it, so the server refunds in full
    regardless of the listing's policy.
  */
  const cancelBooking = useMutation({
    mutationFn: (id: string) => hostService.cancelBooking(id),
    onSuccess: () => {
      toast.success("Booking cancelled and the guest refunded in full.");
      qc.invalidateQueries({ queryKey: ["host-bookings"] });
      qc.invalidateQueries({ queryKey: ["listing-reservations"] });
      setOpenRes(null);
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Couldn't cancel this booking")),
  });
  const release = useMutation({
    mutationFn: (id: string) => hostService.unblockDates(id),
    onSuccess: () => {
      toast.success("Dates released — they're bookable again.");
      qc.invalidateQueries({ queryKey: ["listing-reservations"] });
      setOpenRes(null);
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Couldn't release these dates")),
  });

  return (
    <div className="mx-auto max-w-7xl px-4 py-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">Calendar</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Track availability, reservations, and revenue across all your listings.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setBlockOpen(true)}
            className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2 text-sm font-bold text-ink hover:bg-muted"
          >
            <Ban className="h-4 w-4" /> Block dates
          </button>
          <button
            onClick={() => setManualOpen(true)}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-bold text-primary-foreground hover:opacity-90"
          >
            <Plus className="h-4 w-4" /> Manual booking
          </button>
        </div>
      </div>

      {/* Month nav + month total */}
      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-card p-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
            className="grid h-9 w-9 place-items-center rounded-lg border border-border bg-background text-ink hover:bg-muted"
            aria-label="Previous month"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={() => setMonth(startOfMonth(new Date()))}
            className="rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-bold text-ink hover:bg-muted"
          >
            Today
          </button>
          <button
            onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
            className="grid h-9 w-9 place-items-center rounded-lg border border-border bg-background text-ink hover:bg-muted"
            aria-label="Next month"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <p className="ml-2 inline-flex items-center gap-2 text-base font-extrabold text-ink">
            <CalendarDays className="h-4 w-4 text-ink-muted" /> {monthLabel}
          </p>
        </div>
        <div className="inline-flex items-center gap-2 rounded-xl bg-primary/10 px-3 py-1.5 text-sm font-bold text-primary">
          <Wallet className="h-4 w-4" /> {formatUsh(totalMonthRevenue)} this month
        </div>
      </div>

      {/* Legend */}
      <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-ink-muted">
        <Legend swatch="bg-card border border-border" label="Available" />
        <Legend swatch="bg-primary" label="Confirmed / checked-in" />
        <Legend swatch="bg-accent/55" label="Pending" />
        <Legend swatch="bg-destructive/15" label="Cancelled / rejected" />
      </div>

      {/* Timeline grid */}
      <div className="mt-4 overflow-hidden rounded-2xl border border-border bg-card">
        {listingsQ.isLoading ? (
          <div className="space-y-2 p-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-12 animate-pulse rounded-xl brand-skeleton" />
            ))}
          </div>
        ) : listings.length === 0 ? (
          <div className="p-10 text-center text-sm text-ink-muted">
            You have no listings yet. Add a property and listings to start tracking availability.
          </div>
        ) : (
          <div className="overflow-x-auto no-scrollbar">
            <div
              className="grid min-w-max"
              style={{
                gridTemplateColumns: `220px 110px repeat(${days.length}, 36px)`,
              }}
            >
              {/* Header row */}
              <div className="sticky left-0 z-10 border-b border-r border-border bg-card px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-ink-subtle">
                Listing
              </div>
              <div className="border-b border-r border-border bg-card px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-ink-subtle">
                Revenue
              </div>
              {days.map((d) => {
                const date = parseYMD(d);
                const isToday = d === ymd(new Date());
                const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                return (
                  <div
                    key={d}
                    className={`border-b border-border px-1 py-2 text-center text-[10px] font-bold ${
                      isWeekend ? "bg-muted/40" : "bg-card"
                    } ${isToday ? "text-primary" : "text-ink-muted"}`}
                  >
                    <div className="text-[9px] uppercase">
                      {date.toLocaleDateString(undefined, { weekday: "short" }).slice(0, 1)}
                    </div>
                    <div className={isToday ? "text-primary" : "text-ink"}>{date.getDate()}</div>
                  </div>
                );
              })}

              {/* Listing rows */}
              {listings.map((l) => {
                const reservations = reservationsByListing.get(l.id) ?? [];
                const revenue = revenueByListing.get(l.id) ?? 0;
                return (
                  <ListingRow
                    key={l.id}
                    listing={l}
                    days={days}
                    reservations={reservations}
                    revenue={revenue}
                    onPickReservation={(r) => setOpenRes({ res: r, listing: l })}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Full month calendar view */}
      <MonthGrid
        month={month}
        listings={listings}
        reservationsByListing={reservationsByListing}
        onPickReservation={(r: Reservation, l: Listing) => setOpenRes({ res: r, listing: l })}
      />

      {/* Reservation drawer */}
      <ReservationSheet
        open={!!openRes}
        onClose={() => setOpenRes(null)}
        reservation={openRes?.res ?? null}
        listing={openRes?.listing ?? null}
        booking={openRes?.res.booking_id ? (bookingById.get(openRes.res.booking_id) ?? null) : null}
        onConfirm={(id) => confirm.mutate(id)}
        onReject={(id) => reject.mutate(id)}
        onRelease={(id) => release.mutate(id)}
        onCancelBooking={(id) => cancelBooking.mutate(id)}
        onEditDates={(id) => setEditDatesFor(id)}
        confirming={confirm.isPending}
        rejecting={reject.isPending}
        releasing={release.isPending}
        cancelling={cancelBooking.isPending}
      />

      {editDatesFor && (
        <HostChangeDatesDialog
          bookingId={editDatesFor}
          onClose={() => setEditDatesFor(null)}
          onChanged={() => {
            qc.invalidateQueries({ queryKey: ["host-bookings"] });
            qc.invalidateQueries({ queryKey: ["listing-reservations"] });
            setOpenRes(null);
          }}
        />
      )}

      <ManualBookingDialog
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        listings={listings}
      />
      <BlockDatesDialog open={blockOpen} onClose={() => setBlockOpen(false)} listings={listings} />
    </div>
  );
}

// ---------------- subcomponents ----------------

function Legend({ swatch, label }: { swatch: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`inline-block h-3 w-3 rounded-sm ${swatch}`} />
      {label}
    </span>
  );
}

function ListingRow({
  listing,
  days,
  reservations,
  revenue,
  onPickReservation,
}: {
  listing: Listing;
  days: string[];
  reservations: Reservation[];
  revenue: number;
  onPickReservation: (r: Reservation) => void;
}) {
  // Build a per-day map: day -> reservation that "owns" this day (checkout day excluded).
  const dayMap = new Map<string, Reservation>();
  for (const r of reservations) {
    const start = r.check_in_date;
    const endExclusive = r.check_out_date;
    for (const d of days) {
      if (d >= start && d < endExclusive) dayMap.set(d, r);
    }
  }

  return (
    <>
      <div className="sticky left-0 z-10 flex items-center gap-3 border-b border-r border-border bg-card px-3 py-2">
        <div className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-lg bg-muted text-xs font-bold text-ink-muted">
          {listing.images?.[0] ? (
            <img src={listing.images[0]} alt="" className="h-full w-full object-cover" />
          ) : (
            listing.title.slice(0, 1).toUpperCase()
          )}
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-bold text-ink">{listing.title}</p>
          <p className="truncate text-[11px] text-ink-muted">
            {listing.property?.name ?? "—"} · {formatUsh(listing.price_per_night)}/n
          </p>
        </div>
      </div>
      <div className="flex items-center justify-end border-b border-r border-border bg-card px-3 py-2 text-sm font-bold text-ink">
        {formatUsh(revenue)}
      </div>
      {days.map((d) => {
        const r = dayMap.get(d);
        const date = parseYMD(d);
        const isWeekend = date.getDay() === 0 || date.getDay() === 6;
        const isFirst = r && (d === r.check_in_date || days.indexOf(d) === 0);
        const isLast =
          r &&
          (parseYMD(d).getTime() + 86_400_000 === parseYMD(r.check_out_date).getTime() ||
            days.indexOf(d) === days.length - 1);
        if (r) {
          return (
            <button
              key={d}
              onClick={() => onPickReservation(r)}
              className={`m-[2px] h-9 border ${STATUS_CLASSES[r.status]} text-[10px] font-bold transition hover:brightness-110 ${
                isFirst ? "rounded-l-md" : ""
              } ${isLast ? "rounded-r-md" : ""}`}
              title={`${r.status.replace("_", " ")} · ${r.check_in_date} → ${r.check_out_date}`}
            >
              {isFirst ? "•" : ""}
            </button>
          );
        }
        return (
          <div
            key={d}
            className={`m-[2px] h-9 rounded-md border border-dashed border-border ${
              isWeekend ? "bg-muted/30" : "bg-background"
            }`}
          />
        );
      })}
    </>
  );
}

function MonthGrid({
  month,
  listings,
  reservationsByListing,
  onPickReservation,
}: {
  month: Date;
  listings: Listing[];
  reservationsByListing: Map<string, Reservation[]>;
  onPickReservation: (r: Reservation, l: Listing) => void;
}) {
  // Build a flat list of {reservation, listing} pairs for quick lookup per day.
  const cellsByDay = useMemo(() => {
    const map = new Map<string, { r: Reservation; l: Listing }[]>();
    for (const l of listings) {
      const reservations = reservationsByListing.get(l.id) ?? [];
      for (const r of reservations) {
        // Iterate each night in [check_in, check_out)
        const start = parseYMD(r.check_in_date);
        const end = parseYMD(r.check_out_date);
        for (let t = start.getTime(); t < end.getTime(); t += 86_400_000) {
          const key = ymd(new Date(t));
          if (!map.has(key)) map.set(key, []);
          map.get(key)!.push({ r, l });
        }
      }
    }
    return map;
  }, [listings, reservationsByListing]);

  const monthStart = startOfMonth(month);
  const firstWeekday = monthStart.getDay(); // 0=Sun
  const total = daysInMonth(month);

  // 6-row grid (max possible) of 7 cols. Pad start with prev-month spillover, end with next.
  const cells: { date: Date; inMonth: boolean }[] = [];
  // Leading blanks from previous month
  for (let i = 0; i < firstWeekday; i++) {
    const d = new Date(monthStart.getFullYear(), monthStart.getMonth(), -(firstWeekday - 1 - i));
    cells.push({ date: d, inMonth: false });
  }
  for (let i = 1; i <= total; i++) {
    cells.push({ date: new Date(month.getFullYear(), month.getMonth(), i), inMonth: true });
  }
  // Pad to a multiple of 7 (and at least 6 rows for stable height)
  while (cells.length % 7 !== 0 || cells.length < 42) {
    const last = cells[cells.length - 1].date;
    const d = new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1);
    cells.push({ date: d, inMonth: d.getMonth() === month.getMonth() });
  }

  const todayKey = ymd(new Date());
  const weekdayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div className="mt-6 overflow-hidden rounded-2xl border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div>
          <h2 className="text-base font-extrabold text-ink">Month view</h2>
          <p className="text-xs text-ink-muted">
            All reservations for{" "}
            {month.toLocaleDateString(undefined, { month: "long", year: "numeric" })}. Click any
            chip to manage it.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-7 border-b border-border bg-muted/30">
        {weekdayLabels.map((w) => (
          <div
            key={w}
            className="px-2 py-2 text-center text-[11px] font-bold uppercase tracking-wider text-ink-subtle"
          >
            {w}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {cells.map((cell, idx) => {
          const key = ymd(cell.date);
          const items = cellsByDay.get(key) ?? [];
          const isToday = key === todayKey;
          const isWeekend = cell.date.getDay() === 0 || cell.date.getDay() === 6;
          return (
            <div
              key={`${key}-${idx}`}
              className={`min-h-[96px] border-b border-r border-border p-1.5 ${
                cell.inMonth ? (isWeekend ? "bg-muted/20" : "bg-card") : "bg-muted/10"
              }`}
            >
              <div className="mb-1 flex items-center justify-between">
                <span
                  className={`grid h-6 w-6 place-items-center rounded-full text-[11px] font-bold ${
                    isToday
                      ? "bg-primary text-primary-foreground"
                      : cell.inMonth
                        ? "text-ink"
                        : "text-ink-subtle"
                  }`}
                >
                  {cell.date.getDate()}
                </span>
                {items.length > 0 && (
                  <span className="text-[10px] font-bold text-ink-muted">{items.length}</span>
                )}
              </div>
              <div className="space-y-1">
                {items.slice(0, 3).map(({ r, l }, i) => (
                  <button
                    key={`${r.id}-${i}`}
                    onClick={() => onPickReservation(r, l)}
                    className={`block w-full truncate rounded border px-1.5 py-0.5 text-left text-[10px] font-bold transition hover:brightness-110 ${STATUS_CLASSES[r.status]}`}
                    title={`${l.title} · ${r.status.replace("_", " ")}`}
                  >
                    {l.title}
                  </button>
                ))}
                {items.length > 3 && (
                  <div className="px-1 text-[10px] font-bold text-ink-muted">
                    +{items.length - 3} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * A host rescheduling one of their own bookings.
 *
 * Loads the booking on demand rather than threading it through the calendar's
 * state: the calendar works in reservations (which include blocks), and only a
 * real booking can have its dates moved.
 */
function HostChangeDatesDialog({
  bookingId,
  onClose,
  onChanged,
}: {
  bookingId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { data } = useQuery({
    queryKey: ["booking", bookingId],
    queryFn: () => bookingService.get(bookingId),
  });

  if (!data?.data) return null;

  return (
    <ChangeDatesDialog open onClose={onClose} booking={data.data} onChanged={onChanged} asHost />
  );
}

function ReservationSheet({
  open,
  onClose,
  reservation,
  listing,
  booking,
  onConfirm,
  onReject,
  onRelease,
  onCancelBooking,
  onEditDates,
  confirming,
  rejecting,
  releasing,
  cancelling,
}: {
  open: boolean;
  onClose: () => void;
  reservation: Reservation | null;
  listing: Listing | null;
  booking: Booking | null;
  onConfirm: (id: string) => void;
  onReject: (id: string) => void;
  onRelease: (id: string) => void;
  onCancelBooking: (id: string) => void;
  onEditDates: (id: string) => void;
  confirming: boolean;
  rejecting: boolean;
  releasing: boolean;
  cancelling: boolean;
}) {
  if (!reservation || !listing) return null;
  const nights = nightsBetweenISO(reservation.check_in_date, reservation.check_out_date);
  const isBlock = reservation.status === "blocked";
  // A block earns nothing — showing it as revenue would inflate the month.
  const subtotal = isBlock ? 0 : nights * Number(listing.price_per_night);
  const isPending = reservation.status === "pending";

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="text-ink">{listing.title}</SheetTitle>
          <SheetDescription>
            {reservation.check_in_date} → {reservation.check_out_date} · {nights} night
            {nights === 1 ? "" : "s"}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-3 text-sm">
          <Row label="Status">
            <span
              className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider ${STATUS_CLASSES[reservation.status]}`}
            >
              {reservation.status.replace("_", " ")}
            </span>
          </Row>
          {!isBlock && <Row label="Guests">{reservation.guest_count}</Row>}
          {!isBlock && <Row label="Revenue">{formatUsh(subtotal)}</Row>}
          {booking?.guest_name && <Row label="Guest">{booking.guest_name}</Row>}
          {booking?.guest_phone && <Row label="Phone">{booking.guest_phone}</Row>}
          {booking?.is_host_created && (
            <p className="text-xs text-ink-subtle">Entered by you for an offline guest.</p>
          )}
          {isBlock && (
            <div className="rounded-xl border border-border bg-muted/30 p-3 text-xs text-ink">
              {reservation.reason
                ? `Blocked: ${reservation.reason}`
                : "These nights are blocked and cannot be booked."}
            </div>
          )}
          {booking?.notes && (
            <div className="rounded-xl border border-border bg-muted/30 p-3 text-xs text-ink">
              <span className="font-bold">Note:</span> {booking.notes}
            </div>
          )}
        </div>

        <div className="mt-6 grid grid-cols-2 gap-2">
          {isBlock && (
            <button
              onClick={() => onRelease(reservation.id)}
              disabled={releasing}
              className="col-span-2 inline-flex items-center justify-center gap-1.5 rounded-xl bg-primary px-3 py-2.5 text-sm font-bold text-primary-foreground hover:opacity-90 disabled:opacity-60"
            >
              <Trash2 className="h-4 w-4" />
              {releasing ? "Releasing…" : "Release these dates"}
            </button>
          )}
          {isPending && reservation.booking_id && (
            <>
              <button
                onClick={() => onConfirm(reservation.booking_id!)}
                disabled={confirming}
                className="col-span-1 inline-flex items-center justify-center gap-1.5 rounded-xl bg-primary px-3 py-2.5 text-sm font-bold text-primary-foreground hover:opacity-90 disabled:opacity-60"
              >
                <Check className="h-4 w-4" /> Confirm
              </button>
              <button
                onClick={() => onReject(reservation.booking_id!)}
                disabled={rejecting}
                className="col-span-1 inline-flex items-center justify-center gap-1.5 rounded-xl border border-destructive/40 px-3 py-2.5 text-sm font-bold text-destructive hover:bg-destructive/5 disabled:opacity-60"
              >
                <X className="h-4 w-4" /> Reject
              </button>
            </>
          )}
          {!isBlock && reservation.booking_id && (
            <>
              <button
                onClick={() => onEditDates(reservation.booking_id!)}
                className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-border bg-card px-3 py-2.5 text-sm font-bold text-ink hover:bg-muted"
              >
                <Pencil className="h-4 w-4" /> Edit dates
              </button>
              <button
                onClick={() => onCancelBooking(reservation.booking_id!)}
                disabled={cancelling}
                className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-destructive/40 px-3 py-2.5 text-sm font-bold text-destructive hover:bg-destructive/5 disabled:opacity-60"
              >
                <Trash2 className="h-4 w-4" />
                {cancelling ? "Cancelling…" : "Cancel"}
              </button>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between border-b border-border py-2">
      <span className="text-xs font-bold uppercase tracking-wider text-ink-subtle">{label}</span>
      <span className="text-sm font-semibold text-ink">{children}</span>
    </div>
  );
}

// ---------------- dialogs ----------------

function ManualBookingDialog({
  open,
  onClose,
  listings,
}: {
  open: boolean;
  onClose: () => void;
  listings: Listing[];
}) {
  const qc = useQueryClient();
  const [listingId, setListingId] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [guestCount, setGuestCount] = useState(1);
  const [notes, setNotes] = useState("");

  const create = useMutation({
    mutationFn: () =>
      hostService.createManualBooking({
        listing_id: listingId,
        guest_name: name,
        guest_phone: phone || undefined,
        guest_email: email || undefined,
        check_in_date: checkIn,
        check_out_date: checkOut,
        guest_count: guestCount,
        notes: notes || undefined,
      }),
    onSuccess: () => {
      toast.success("Booking created.");
      qc.invalidateQueries({ queryKey: ["host-bookings"] });
      qc.invalidateQueries({ queryKey: ["listing-reservations"] });
      onClose();
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Couldn't create the booking")),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!listingId) return toast.error("Pick a listing.");
    if (!name.trim()) return toast.error("Enter the guest's name.");
    if (!checkIn || !checkOut || checkOut <= checkIn)
      return toast.error("Pick valid check-in and check-out dates.");
    create.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Manual booking</DialogTitle>
          <DialogDescription>
            Add a booking for an offline guest. They won't get an account, but the dates will be
            marked as reserved.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Listing">
            <Select value={listingId} onValueChange={setListingId}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a listing" />
              </SelectTrigger>
              <SelectContent>
                {listings.map((l) => (
                  <SelectItem key={l.id} value={l.id}>
                    {l.title} · {formatUsh(l.price_per_night)}/n
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Guest name">
            <Input value={name} onChange={setName} placeholder="e.g. Jane Mukasa" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone">
              <Input value={phone} onChange={setPhone} placeholder="+256 …" />
            </Field>
            <Field label="Email (optional)">
              <Input value={email} onChange={setEmail} placeholder="guest@email.com" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Check-in">
              <DateInput value={checkIn} onChange={setCheckIn} />
            </Field>
            <Field label="Check-out">
              <DateInput value={checkOut} onChange={setCheckOut} min={checkIn || undefined} />
            </Field>
          </div>
          <Field label="Guests">
            <Input
              type="number"
              value={String(guestCount)}
              onChange={(v) => setGuestCount(Math.max(1, Number(v) || 1))}
            />
          </Field>
          <Field label="Notes (optional)">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="block w-full rounded-xl border border-input bg-background px-3 py-2 text-sm text-ink outline-none focus:border-primary"
            />
          </Field>
          <DialogFooter>
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-border bg-card px-3 py-2 text-sm font-bold text-ink hover:bg-muted"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={create.isPending}
              className="rounded-xl bg-primary px-4 py-2 text-sm font-bold text-primary-foreground hover:opacity-90 disabled:opacity-60"
            >
              {create.isPending ? "Creating…" : "Create booking"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BlockDatesDialog({
  open,
  onClose,
  listings,
}: {
  open: boolean;
  onClose: () => void;
  listings: Listing[];
}) {
  const qc = useQueryClient();
  const [listingId, setListingId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [reason, setReason] = useState("");

  const block = useMutation({
    mutationFn: () =>
      hostService.blockDates({
        listing_id: listingId,
        check_in_date: from,
        check_out_date: to,
        reason: reason || undefined,
      }),
    onSuccess: () => {
      toast.success("Dates blocked.");
      qc.invalidateQueries({ queryKey: ["listing-reservations"] });
      onClose();
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Couldn't block those dates")),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!listingId) return toast.error("Pick a listing.");
    if (!from || !to || to <= from) return toast.error("Pick a valid date range.");
    block.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Block dates</DialogTitle>
          <DialogDescription>
            Mark dates as unavailable (maintenance, personal use). No booking will be created.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Listing">
            <Select value={listingId} onValueChange={setListingId}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a listing" />
              </SelectTrigger>
              <SelectContent>
                {listings.map((l) => (
                  <SelectItem key={l.id} value={l.id}>
                    {l.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="From">
              <DateInput value={from} onChange={setFrom} />
            </Field>
            <Field label="To">
              <DateInput value={to} onChange={setTo} min={from || undefined} />
            </Field>
          </div>
          <Field label="Reason (optional)">
            <Input value={reason} onChange={setReason} placeholder="Maintenance, personal use…" />
          </Field>
          <DialogFooter>
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-border bg-card px-3 py-2 text-sm font-bold text-ink hover:bg-muted"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={block.isPending}
              className="rounded-xl bg-primary px-4 py-2 text-sm font-bold text-primary-foreground hover:opacity-90 disabled:opacity-60"
            >
              {block.isPending ? "Blocking…" : "Block dates"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-ink-muted">
        {label}
      </span>
      {children}
    </label>
  );
}

function Input({
  value,
  onChange,
  placeholder,
  type = "text",
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="block h-10 w-full rounded-xl border border-input bg-background px-3 text-sm text-ink outline-none focus:border-primary"
    />
  );
}

function DateInput({
  value,
  onChange,
  min,
}: {
  value: string;
  onChange: (v: string) => void;
  min?: string;
}) {
  return (
    <input
      type="date"
      value={value}
      min={min}
      onChange={(e) => onChange(e.target.value)}
      className="block h-10 w-full rounded-xl border border-input bg-background px-3 text-sm text-ink outline-none focus:border-primary"
    />
  );
}
